import { useState, useMemo } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { cn } from '@/lib/utils'
import { useTheme } from '@/contexts/ThemeContext'
import { authStore } from '@/stores/AuthStore'
import {
  LayoutDashboard,
  Bot,
  Users,
  Layers,
  Play,
  Sparkles,
  Box,
  FlaskConical,
  Database,
  CpuIcon,
  Server,
  Activity,
  Settings,
  PanelLeftClose,
  PanelLeft,
  FileText,
  BookOpen,
  Bell,
  LogOut,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react'

const SIDEBAR_STORAGE_KEY = 'agent-dashboard-sidebar-collapsed'

export interface NavigationItem {
  name: string
  to: string
  icon: React.ComponentType<{ className?: string }>
}

/**
 * Canonical navigation list. The Sidebar renders a filtered and reordered
 * version of this based on useTheme().hiddenPages / pageOrder.
 * Exported so SettingsPage → SidebarSettings can iterate over it too.
 */
export const NAVIGATION: NavigationItem[] = [
  // Overview
  { name: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  // Agents & Teams
  { name: 'Agents', to: '/agents', icon: Bot },
  { name: 'Agent Teams', to: '/agent-teams', icon: Users },
  // Operations (merged Executions + Audits + PII)
  { name: 'Operations', to: '/operations', icon: Play },
  // Advanced Execution
  { name: 'Playground', to: '/playground', icon: Sparkles },
  { name: 'Agent Architect', to: '/architect', icon: Box },
  { name: 'Workflows', to: '/workflows', icon: Layers },
  // Simulation
  { name: 'Simulation', to: '/simulation', icon: FlaskConical },
  // Integrations
  { name: 'Editor AI', to: '/wysiwyg', icon: FileText },
  // Data & System
  { name: 'Data', to: '/data', icon: Database },
  { name: 'MCP Servers', to: '/mcp', icon: Server },
  { name: 'System Status', to: '/system', icon: Activity },
]

function getStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export function useSidebarState() {
  const [isCollapsed, setIsCollapsed] = useState(getStoredCollapsed)

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next))
      } catch {
        // ignore
      }
      return next
    })
  }

  return { isCollapsed, toggleCollapsed }
}

interface SidebarProps {
  isCollapsed: boolean
  onToggleCollapsed: () => void
}

const Sidebar = observer(({ isCollapsed, onToggleCollapsed }: SidebarProps) => {
  const navigate = useNavigate()
  const { theme, setTheme, hiddenPages, pageOrder } = useTheme()

  // Theme cycle: light → dark → system → light …
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor
  const cycleTheme = () => {
    if (theme === 'light') setTheme('dark')
    else if (theme === 'dark') setTheme('system')
    else setTheme('light')
  }

  const handleLogout = () => {
    authStore.logout()
    navigate('/login')
  }

  // Apply user's hiddenPages + pageOrder to NAVIGATION
  const visibleNav = useMemo<NavigationItem[]>(() => {
    const filtered = NAVIGATION.filter((item) => !hiddenPages.includes(item.to))
    if (pageOrder.length === 0) return filtered
    return [...filtered].sort((a, b) => {
      const ai = pageOrder.indexOf(a.to)
      const bi = pageOrder.indexOf(b.to)
      if (ai === -1 && bi === -1) return 0
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
  }, [hiddenPages, pageOrder])

  return (
    <div
      className={cn(
        'fixed inset-y-0 left-0 z-50 hidden flex-col bg-card border-r border-border transition-all duration-300 lg:flex',
        isCollapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo area */}
      <div
        className={cn(
          'flex h-14 items-center border-b border-border shrink-0',
          isCollapsed ? 'justify-center px-2' : 'justify-between px-4'
        )}
      >
        <div className={cn('flex items-center gap-2', isCollapsed && 'justify-center')}>
          <CpuIcon className="h-7 w-7 text-primary shrink-0" />
          {!isCollapsed && (
            <span className="text-base font-bold text-primary truncate">
              AI Agent Platform
            </span>
          )}
        </div>
        {!isCollapsed && (
          <button
            onClick={onToggleCollapsed}
            className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav
        className="flex-1 flex flex-col gap-1 p-2 overflow-y-auto min-h-0"
      >
        {visibleNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/dashboard'}
            title={isCollapsed ? item.name : undefined}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isCollapsed && 'justify-center px-2',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )
            }
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {!isCollapsed && item.name}
          </NavLink>
        ))}
      </nav>

      {/* Footer — user profile + action buttons (was previously the top Header) */}
      <div className="border-t border-border p-2 shrink-0">
        {!isCollapsed ? (
          <>
            {/* User profile */}
            <div className="flex items-center gap-3 px-1 py-2">
              <div className="h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center text-primary text-sm font-semibold shrink-0">
                {(authStore.user?.full_name || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-foreground leading-tight">
                  {authStore.user?.full_name || 'User'}
                </p>
                <p className="truncate text-xs text-muted-foreground leading-tight">
                  {authStore.user?.email || ''}
                </p>
              </div>
            </div>

            {/* Action row */}
            <div className="flex items-center justify-between px-1 pt-1 border-t border-border/60">
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => navigate('/docs')}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  title="Documentation"
                >
                  <BookOpen className="h-4 w-4" />
                </button>
                <button
                  onClick={() => navigate('/inbox')}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  title="Notifications"
                >
                  <Bell className="h-4 w-4" />
                </button>
                <button
                  onClick={cycleTheme}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  title={`Theme: ${theme} (click to cycle)`}
                >
                  <ThemeIcon className="h-4 w-4" />
                </button>
                <NavLink
                  to="/settings"
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  title="Settings"
                >
                  <Settings className="h-4 w-4" />
                </NavLink>
              </div>
              {authStore.isAuthenticated && (
                <button
                  onClick={handleLogout}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                  title="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              )}
            </div>
          </>
        ) : (
          /* Collapsed footer: stack of icon buttons */
          <div className="flex flex-col items-center gap-1">
            <div className="h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-semibold">
              {(authStore.user?.full_name || 'U').charAt(0).toUpperCase()}
            </div>
            <button
              onClick={cycleTheme}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              title={`Theme: ${theme}`}
            >
              <ThemeIcon className="h-4 w-4" />
            </button>
            <NavLink
              to="/settings"
              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              title="Settings"
            >
              <Settings className="h-4 w-4" />
            </NavLink>
            <button
              onClick={onToggleCollapsed}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              title="Expand sidebar"
            >
              <PanelLeft className="h-4 w-4" />
            </button>
            {authStore.isAuthenticated && (
              <button
                onClick={handleLogout}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

export default Sidebar
